import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { Router } from '@angular/router';
import { NgForage } from 'ngforage';
import { of, Subject } from 'rxjs';
import { catchError, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { ProductTypes } from 'src/app/model/product/product-type';
import { ProperPageResult } from 'src/app/model/proper-page-result';
import { PAGE_SIZE_OPTIONS } from 'src/app/services/page-index-service';
import { SubscriptionWithDestroyTriggerComponent } from 'src/app/subscription-helpers/subscription-with-destroy-trigger.component';
import { SearchSubProjectsInput } from '../input/helper/search-subprojects-input';
import { SearchSubProjectsInputHelper } from '../input/helper/search-subprojects-input-helper';
import { SearchSubProject } from './model/search-sub-project';
import { SearchSubProjectsControllerRequestService } from './search-sub-projects-controller-request.service';

@Component({
  selector: 'search-subprojects-result',
  templateUrl: './search-subprojects-result.component.html',
  styleUrls: ['./search-subprojects-result.component.scss'],
})
export class SearchSubProjectsResultComponent
  extends SubscriptionWithDestroyTriggerComponent
  implements OnInit, OnDestroy
{
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  loading: boolean = true;

  searchInput: SearchSubProjectsInput;

  totalItems = 0;
  subProjects: SearchSubProject[];

  sortColumn: string = 'subProjects.currentCreatedAt';
  sortAscending: boolean = false;

  pageSizeOptions = PAGE_SIZE_OPTIONS;

  displayedColumns = [
    'projectId',
    'currentCreatedAt',
    'companyName',
    'address',
    'product',
    'type',
    'measurements',
    'currentExecutionDate',
    'status',
  ];

  getSubProjectsSubject = new Subject<any>();

  constructor(
    private searchSubProjectsControllerRequestService: SearchSubProjectsControllerRequestService,
    private ngForage: NgForage,
    private router: Router
  ) {
    super();
  }

  ngOnInit(): void {
    this.doInitialize();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.getSubProjectsSubject != null) {
      this.getSubProjectsSubject.complete();
      this.getSubProjectsSubject = null;
    }
  }

  private async doInitialize(): Promise<void> {
    this.paginator.pageIndex = 0;
    this.paginator.pageSize = 30;

    this.searchInput = await this.ngForage.getItem(
      SearchSubProjectsInputHelper.INPUT_STORE_KEY
    );

    this.addSubscription(
      this.getSubProjectsSubject.pipe(
        startWith({}),
        switchMap(async () => {
          this.loading = true;
          return await this.searchSubProjectsControllerRequestService.searchSubProjects(
            this.searchInput,
            this.sortColumn,
            this.sortAscending,
            this.paginator.pageSize,
            this.paginator.pageIndex
          );
        }),
        catchError(() => {
          return of({});
        }),
        takeUntil(this.destroyThisComponent)
      ),
      (res: ProperPageResult<SearchSubProject>) => {
        if (res != null) {
          this.processSubProjects(res.items);
        }
        this.subProjects = res == null ? [] : res.items;
        this.totalItems = res == null ? 0 : res.totalItems;
        this.loading = false;
      }
    );
  }

  private processSubProjects(subProjects: SearchSubProject[]): void {
    if (CollectionHelper.isPopulated(subProjects)) {
      subProjects.forEach((sp) => {
        if (
          sp.project != null &&
          CollectionHelper.isPopulated(sp.project.projectContacts)
        ) {
          sp.project.companyNames = [
            ...new Set<string>(
              sp.project.projectContacts.map((pc) =>
                pc.company == null ? null : pc.company.regularName
              )
            ),
          ];
        }
      });
    }
  }

  openProject(subProject: SearchSubProject): void {
    const containsIPBoard =
      subProject.project != null &&
      CollectionHelper.isPopulated(subProject.project.products) &&
      subProject.project.products.some(
        (p) => p.entityType === ProductTypes.IP_BOARD.value
      );
    this.router.navigate([
      containsIPBoard ? 'job' : 'project',
      subProject.project.id,
      subProject.id,
    ]);
  }
}
